/**
 * Control Portal - Integration Management
 * Configure ERP integrations, webhooks, API clients per tenant
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, Plug, Webhook, Key, Shield, RefreshCw, Loader2,
  Settings, Check, X, Clock, AlertTriangle, ExternalLink, Eye
} from 'lucide-react';

export default function TenantIntegrationsPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [summary, setSummary] = useState({});
  const [clients, setClients] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  
  // Dialog states
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [deliveriesDialogOpen, setDeliveriesDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tenantRes, summaryRes, clientsRes, webhooksRes] = await Promise.all([
        controlApi.getTenant(tenantId),
        controlApi.getIntegrationSummary(tenantId),
        controlApi.getIntegrationClients(tenantId, { limit: 100 }),
        controlApi.getWebhooks(tenantId, { limit: 100 })
      ]);
      setTenant(tenantRes.data);
      setSummary(summaryRes.data);
      setClients(clientsRes.data);
      setWebhooks(webhooksRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const openClientDialog = (client) => {
    setSelectedClient(client);
    setClientDialogOpen(true);
  };

  const openWebhookDialog = (webhook) => {
    setSelectedWebhook(webhook);
    setWebhookDialogOpen(true);
  };

  const openDeliveriesDialog = async (webhook) => {
    setSelectedWebhook(webhook);
    try {
      const res = await controlApi.getWebhookDeliveries(tenantId, webhook.id, { limit: 50 });
      setDeliveries(res.data);
      setDeliveriesDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load deliveries');
    }
  };

  const handleToggleClient = async (client) => {
    try {
      await controlApi.updateIntegrationClient(tenantId, client.id, {
        is_active: !client.is_active
      });
      toast.success(`Client ${client.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const handleToggleWebhook = async (webhook) => {
    try {
      await controlApi.updateWebhook(tenantId, webhook.id, {
        is_active: !webhook.is_active
      });
      toast.success(`Webhook ${webhook.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  const handleUpdateClientSettings = async () => {
    setSaving(true);
    try {
      await controlApi.updateIntegrationClient(tenantId, selectedClient.id, {
        rate_limit_rpm: selectedClient.rate_limit_rpm,
        rate_limit_burst: selectedClient.rate_limit_burst,
        allowed_ips: selectedClient.allowed_ips
      });
      toast.success('Client settings updated');
      setClientDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="tenant-integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/control/tenants/${tenantId}`)}
            className="text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Integration Management</h1>
            <p className="text-slate-400">{tenant?.name} - ERP & Webhook Configuration</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-slate-700 text-slate-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
              <Key className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.clients?.active || 0}</p>
              <p className="text-slate-400 text-sm">Active Clients</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
              <Webhook className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.webhooks?.active || 0}</p>
              <p className="text-slate-400 text-sm">Active Webhooks</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-teal-500/20">
              <Check className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.deliveries_24h?.sent || 0}</p>
              <p className="text-slate-400 text-sm">Deliveries (24h)</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/20">
              <X className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.deliveries_24h?.failed || 0}</p>
              <p className="text-slate-400 text-sm">Failed (24h)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="clients" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="clients" className="data-[state=active]:bg-blue-600">
            <Key className="w-4 h-4 mr-2" />
            API Clients
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="data-[state=active]:bg-blue-600">
            <Webhook className="w-4 h-4 mr-2" />
            Webhooks
          </TabsTrigger>
        </TabsList>

        {/* API Clients Tab */}
        <TabsContent value="clients">
          <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-slate-800/50">
                  <TableHead className="text-slate-400">Client Name</TableHead>
                  <TableHead className="text-slate-400">System</TableHead>
                  <TableHead className="text-slate-400">Client ID</TableHead>
                  <TableHead className="text-slate-400">Rate Limit</TableHead>
                  <TableHead className="text-slate-400">Last Used</TableHead>
                  <TableHead className="text-slate-400 text-center">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No integration clients configured</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow key={client.id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell className="text-white font-medium">{client.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-600">
                          {client.system_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 font-mono text-sm">
                        {client.client_id.substring(0, 20)}...
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {client.rate_limit_rpm || 60}/min
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {client.last_used_at ? new Date(client.last_used_at).toLocaleString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={client.is_active}
                          onCheckedChange={() => handleToggleClient(client)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openClientDialog(client)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks">
          <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-slate-800/50">
                  <TableHead className="text-slate-400">Webhook Name</TableHead>
                  <TableHead className="text-slate-400">URL</TableHead>
                  <TableHead className="text-slate-400">Events</TableHead>
                  <TableHead className="text-slate-400">Deliveries</TableHead>
                  <TableHead className="text-slate-400 text-center">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhooks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Webhook className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No webhooks configured</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  webhooks.map((webhook) => (
                    <TableRow key={webhook.id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell className="text-white font-medium">{webhook.name || 'Unnamed'}</TableCell>
                      <TableCell className="text-slate-300 text-sm max-w-[200px] truncate">
                        {webhook.url}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(webhook.events || []).slice(0, 2).map((event, i) => (
                            <Badge key={i} variant="outline" className="border-slate-600 text-xs">
                              {event}
                            </Badge>
                          ))}
                          {webhook.events?.length > 2 && (
                            <Badge variant="outline" className="border-slate-600 text-xs">
                              +{webhook.events.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-green-400">{webhook.stats?.sent || 0}</span>
                          <span className="text-slate-500">/</span>
                          <span className="text-red-400">{webhook.stats?.failed || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={webhook.is_active}
                          onCheckedChange={() => handleToggleWebhook(webhook)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeliveriesDialog(webhook)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Client Settings Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Client Settings</DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure rate limits and IP allowlist
            </DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Client Name</Label>
                <p className="text-white mt-1">{selectedClient.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Rate Limit (RPM)</Label>
                  <Input
                    type="number"
                    value={selectedClient.rate_limit_rpm || 60}
                    onChange={(e) => setSelectedClient({ ...selectedClient, rate_limit_rpm: parseInt(e.target.value) })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Burst</Label>
                  <Input
                    type="number"
                    value={selectedClient.rate_limit_burst || 10}
                    onChange={(e) => setSelectedClient({ ...selectedClient, rate_limit_burst: parseInt(e.target.value) })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">IP Allowlist (comma-separated)</Label>
                <Input
                  value={(selectedClient.allowed_ips || []).join(', ')}
                  onChange={(e) => setSelectedClient({ ...selectedClient, allowed_ips: e.target.value.split(',').map(ip => ip.trim()).filter(Boolean) })}
                  placeholder="10.0.0.1, 192.168.1.0/24"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
                <p className="text-slate-500 text-xs mt-1">Leave empty to allow all IPs</p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="ghost" onClick={() => setClientDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUpdateClientSettings} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliveries Dialog */}
      <Dialog open={deliveriesDialogOpen} onOpenChange={setDeliveriesDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Webhook Deliveries</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedWebhook?.name || 'Webhook'} - Recent delivery attempts
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Event</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Attempts</TableHead>
                  <TableHead className="text-slate-400">Response</TableHead>
                  <TableHead className="text-slate-400">Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      No deliveries found
                    </TableCell>
                  </TableRow>
                ) : (
                  deliveries.map((d) => (
                    <TableRow key={d.id} className="border-slate-800">
                      <TableCell className="text-white text-sm">{d.event_type}</TableCell>
                      <TableCell>
                        <Badge variant={d.status === 'sent' ? 'default' : d.status === 'pending' ? 'secondary' : 'destructive'}>
                          {d.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300">{d.attempts}</TableCell>
                      <TableCell className="text-slate-300">{d.last_response_code || '-'}</TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {new Date(d.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
